import { registerWeapon } from '../../weapons';
import { SMALL_CRATER } from '../../terrain/crater-shapes';
import { isPointInTerrain, clearCircle } from '../../terrain/grid';
import { recordTrickHit } from '../../game/trick-score';

// ─── Drill Mine: Drill → Blink → Detonate ──────────────────────────────────
// Koma lands and drills ~300px into terrain (wide shaft).
// Pulls nearby tanks down into the shaft.
// Pauses at the bottom with blink-blink-BOOM before Mine-scale detonation.

const DRILLMINE = {
    // Drill phase
    DRILL_SPEED: 3.5,        // px/frame downward (slower, more dramatic)
    DRILL_RADIUS: 30,        // wide shaft
    DRILL_DEPTH: 300,        // target drill depth in px
    DRILL_DAMAGE: 5,         // tick damage while drilling through tanks
    DRILL_INTERVAL: 3,       // frames between terrain carves
    CONTACT_RADIUS: 35,      // wide — pull tanks in
    PULL_FORCE: 3.0,         // downward pull on tanks in range
    HIT_COOLDOWN: 8,         // frames between tick damage on same target

    // Fuse phase (blink-blink-boom at bottom)
    FUSE_FRAMES: 50,         // total pause time at bottom (~0.83s)
    BLINK_1: 15,             // frame of first blink
    BLINK_2: 30,             // frame of second blink
    // Detonation fires at FUSE_FRAMES

    // Detonation
    BLAST_RADIUS: 70,        // Mine-scale explosion at bottom
    BLAST_DAMAGE: 18,        // Mine-scale damage
    BLAST_KNOCKBACK: 0.24,   // 3x Mine-scale knockback — big send
    BLAST_UP_BIAS: 0.7,      // strong upward knockback bias (0-1)

    // Phase IDs
    PHASE_DRILL: 0,
    PHASE_FUSE: 1,
};

export const GYRO_DRILLMINE = registerWeapon({
    id: 'gyro_drillmine',
    name: 'Drill Mine',
    description: '',
    tier: 'SUB',
    rarity: 'common',
    projectileRadius: 8,
    projectileSpeed: 0,
    color: 0x88CC77,
    gravity: 0,
    bounces: 0,
    bounceEnergyRetention: 0,
    damage: DRILLMINE.BLAST_DAMAGE,
    blastRadius: DRILLMINE.BLAST_RADIUS,
    knockbackMult: DRILLMINE.BLAST_KNOCKBACK,
    knockbackRadius: 1.2,
    knockbackAngleBias: { angle: -Math.PI / 2, strength: DRILLMINE.BLAST_UP_BIAS },
    shakeIntensity: 2.5,
    particleScale: 1.5,
    craterShape: SMALL_CRATER,
    canDestroyWireframe: true,
    projectileShape: 'koma',
    onTick(proj, state, fx) {
        const bs = proj.behaviorState;
        bs.lifetime = (bs.lifetime || 0) + 1;

        // Initialize on first frame — start drilling immediately
        if (bs.phase === undefined) {
            bs.phase = DRILLMINE.PHASE_DRILL;
            bs.drillDepth = 0;
            bs.fuseFrame = 0;

            proj.vx = 0;
            proj.vy = 0;

            fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0x88CC77, 3] });
            fx.push({ type: 'screen_shake', intensity: 3, decay: 0.88 });
            fx.push({ type: 'announce_world', text: 'DRILL!', x: proj.x, y: proj.y - 40, color: 0xFF6644, fontSize: 28, hold: 0.5 });
        }

        // ─── PHASE 0: DRILL ─────────────────────────────────────────────
        if (bs.phase === DRILLMINE.PHASE_DRILL) {
            proj.vx = 0;
            proj.vy = DRILLMINE.DRILL_SPEED;
            bs.drillDepth += DRILLMINE.DRILL_SPEED;

            // Carve terrain as we drill
            if (bs.lifetime % DRILLMINE.DRILL_INTERVAL === 0) {
                const carveBounds = clearCircle(proj.x, proj.y, DRILLMINE.DRILL_RADIUS, true);
                if (carveBounds) {
                    fx.push({ type: 'terrain_update', bounds: carveBounds });
                }
            }

            // Debris particles — flying outward
            if (bs.lifetime % 2 === 0) {
                const side = Math.random() > 0.5 ? 1 : -1;
                fx.push({
                    type: 'particles', method: 'spawnExplosion', args: [
                        proj.x + side * DRILLMINE.DRILL_RADIUS * 0.8,
                        proj.y,
                        0x996633, 2,
                    ]
                });
                fx.push({
                    type: 'particles', method: 'spawnExplosion', args: [
                        proj.x + (Math.random() - 0.5) * 8,
                        proj.y - 5,
                        0x88CC77, 1,
                    ]
                });
            }

            // Screen shake while drilling through terrain
            if (bs.lifetime % 4 === 0 && isPointInTerrain(proj.x, proj.y + 10)) {
                fx.push({ type: 'screen_shake', intensity: 4, decay: 0.86 });
            }

            // Tank interaction — pull tanks down + tick damage
            for (let i = 0; i < state.players.length; i++) {
                if (i === proj.owner) continue;
                const p = state.players[i];
                if (!p.alive) continue;

                const dx = p.x - proj.x;
                const dy = p.y - proj.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist < DRILLMINE.CONTACT_RADIUS) {
                    p.vy += DRILLMINE.PULL_FORCE;
                    p.settled = false;

                    const hitKey = `hitCooldown_${i}`;
                    bs[hitKey] = (bs[hitKey] || 0) - 1;
                    if (bs[hitKey] <= 0) {
                        bs[hitKey] = DRILLMINE.HIT_COOLDOWN;
                        p.damagePercent += DRILLMINE.DRILL_DAMAGE;
                        fx.push({ type: 'particles', method: 'spawnHitCross', args: [p.x, p.y, 14] });
                        fx.push({ type: 'tank_flash', playerIndex: i, bright: 0xbbffaa, dark: 0x113300, duration: 0.1 });
                        // Trick recording — each grind tick is a direct hit
                        recordTrickHit(state, proj, i, p.x, p.y, DRILLMINE.DRILL_DAMAGE, true, fx);
                    }
                }
            }

            // Transition to fuse: max depth, void, or exited terrain
            const pastMaxDepth = bs.drillDepth >= DRILLMINE.DRILL_DEPTH;
            const hitVoid = proj.y >= state.voidY;
            const exitedTerrain = bs.lifetime > 10
                && !isPointInTerrain(proj.x, proj.y + 10)
                && !isPointInTerrain(proj.x, proj.y + 20);

            if (pastMaxDepth || hitVoid || exitedTerrain) {
                bs.phase = DRILLMINE.PHASE_FUSE;
                bs.fuseFrame = 0;
                proj.vx = 0;
                proj.vy = 0;
            }

            return null;
        }

        // ─── PHASE 1: FUSE (blink-blink-boom) ──────────────────────────
        if (bs.phase === DRILLMINE.PHASE_FUSE) {
            proj.vx = 0;
            proj.vy = 0;
            bs.fuseFrame++;

            // Blink 1
            if (bs.fuseFrame === DRILLMINE.BLINK_1) {
                fx.push({ type: 'screen_flash', color: 0xFF4444, alpha: 0.08 });
                fx.push({ type: 'screen_shake', intensity: 3, decay: 0.90 });
                fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0xFF4444, 2] });
            }

            // Blink 2 (brighter)
            if (bs.fuseFrame === DRILLMINE.BLINK_2) {
                fx.push({ type: 'screen_flash', color: 0xFF4444, alpha: 0.12 });
                fx.push({ type: 'screen_shake', intensity: 5, decay: 0.88 });
                fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0xFF6644, 3] });
            }

            // BOOM
            if (bs.fuseFrame >= DRILLMINE.FUSE_FRAMES) {
                fx.push({ type: 'announce_world', text: 'DETONATE!', x: proj.x, y: proj.y - 30, color: 0xFF4444, fontSize: 28, hold: 0.6 });
                fx.push({ type: 'screen_shake', intensity: 10, decay: 0.80 });
                fx.push({ type: 'screen_flash', color: 0xFF4444, alpha: 0.15 });
                return 'detonate';
            }

            return null;
        }

        return null;
    },
});
